import { useMemo } from 'react';
import { useImmigrationWorkflowDefinition } from './useImmigrationWorkflowDefinition';

/** Must match backend `gcWorkflow.js` GC_STEPS order. */

export const GC_STEPS = [
  'perm_pwd',
  'perm_adv_state_job_bank',
  'perm_adv_sunday_newspaper_1',
  'perm_adv_sunday_newspaper_2',
  'perm_adv_online_week',
  'perm_adv_company_website',
  'perm_adv_employee_referral',
  'perm_adv_notice_board',
  'perm_adv_complete',
  'perm_filing',
  'perm_pending',
  'perm_certified',
  'i140_prepare',
  'i140_filed',
  'i140_decision',
  'i485_prepare',
  'i485_filed',
  'i485_biometrics',
  'i485_interview',
  'i485_decision',
  'gc_complete',
];

export const GC_UPLOAD_SLOTS = [
  'perm_pwd',
  'perm_adv_state_job_bank',
  'perm_adv_sunday_newspaper_1',
  'perm_adv_sunday_newspaper_2',
  'perm_adv_online_week',
  'perm_adv_company_website',
  'perm_adv_employee_referral',
  'perm_adv_notice_board',
  'perm_adv_other',
  'perm_adv_complete',
  'perm_filing',
  'perm_pending',
  'perm_certified',
  'i140_prepare',
  'i140_filed',
  'i140_decision',
  'i485_prepare',
  'i485_filed',
  'i485_biometrics',
  'i485_interview',
  'i485_decision',
];

export const GC_STEP_META = {
  perm_pwd: {
    label: '1a — Prevailing Wage Determination (PWD)',
    hint: 'Obtain PWD from DOL; store determination PDF, ETA case number, and any screenshots from the FLAG / prevailing wage system.',
    phase: 'perm',
  },
  perm_adv_state_job_bank: {
    label: '1b — State workforce agency job order',
    hint: 'Post on the state job bank where the job is located (e.g. WorkInTexas.com in Texas). Keep URL, posting confirmation, and screenshots showing the live posting and dates.',
    phase: 'perm',
  },
  perm_adv_sunday_newspaper_1: {
    label: '1b — Sunday newspaper advertisement (1 of 2)',
    hint: 'First of two Sunday ads in newspaper of general circulation in the area of intended employment. Upload tearsheets / PDFs and photos of the printed ad.',
    phase: 'perm',
  },
  perm_adv_sunday_newspaper_2: {
    label: '1b — Sunday newspaper advertisement (2 of 2)',
    hint: 'Second Sunday ad (different Sunday than the first, per DOL practice). Upload evidence of publication.',
    phase: 'perm',
  },
  perm_adv_online_week: {
    label: '1b — Online job advertisement (≥ 7 calendar days)',
    hint: 'Professional / national job site or employer website campaign as required; document start/end dates and screenshots across the posting period.',
    phase: 'perm',
  },
  perm_adv_company_website: {
    label: '1b — Company career website posting',
    hint: 'Internal career portal listing; capture screenshots with dates and URL.',
    phase: 'perm',
  },
  perm_adv_employee_referral: {
    label: '1b — In-house / employee referral program posting',
    hint: 'Post on referral program or intranet if used as a recruitment step; upload screenshots or internal notices.',
    phase: 'perm',
  },
  perm_adv_notice_board: {
    label: '1b — On-site notice board',
    hint: 'Physical notice at worksite; photos of the posted notice with date visible.',
    phase: 'perm',
  },
  perm_adv_other: {
    label: '1b — Other recruitment evidence',
    hint: 'Optional slot for extra recruitment channels your counsel requires (e.g. professional journal, radio).',
    phase: 'perm',
  },
  perm_adv_complete: {
    label: '1b — Recruitment summary / audit file',
    hint: 'Compile recruitment report, applicant summary, and final documentation before PERM filing.',
    phase: 'perm',
  },
  perm_filing: {
    label: '1c — PERM / ETA-9089 filing',
    hint: 'File ETA-9089 with DOL; store filed copy, confirmation, and DOL case number when assigned.',
    phase: 'perm',
  },
  perm_pending: {
    label: 'PERM — Pending DOL review',
    hint: 'Track audit or supervised recruitment if issued; upload DOL correspondence.',
    phase: 'perm',
  },
  perm_certified: {
    label: 'PERM — Certified (approved)',
    hint: 'Store certified ETA-9089 and certification notice.',
    phase: 'perm',
  },
  i140_prepare: {
    label: '2 — I-140 petition preparation',
    hint: 'Gather petition, support letters, and exhibits before USCIS filing.',
    phase: 'i140',
  },
  i140_filed: {
    label: '2 — I-140 filed with USCIS',
    hint: 'Upload receipt notice; track premium processing if applicable.',
    phase: 'i140',
  },
  i140_decision: {
    label: '2 — I-140 decision',
    hint: 'Approval (or RFE / denial) notice on file.',
    phase: 'i140',
  },
  i485_prepare: {
    label: '3 — I-485 preparation',
    hint: 'Forms, medical exam, supporting civil documents.',
    phase: 'i485',
  },
  i485_filed: {
    label: '3 — I-485 filed',
    hint: 'Receipt notices, filing package copy.',
    phase: 'i485',
  },
  i485_biometrics: {
    label: '3 — Biometrics appointment',
    hint: 'ASC notice and completion evidence if needed.',
    phase: 'i485',
  },
  i485_interview: {
    label: '3 — Interview (if applicable)',
    hint: 'Interview notice, preparation notes, outcome.',
    phase: 'i485',
  },
  i485_decision: {
    label: '3 — I-485 decision',
    hint: 'Card production / approval notice or other final disposition.',
    phase: 'i485',
  },
  gc_complete: {
    label: 'Green card process complete',
    hint: 'All major milestones recorded.',
    phase: 'done',
  },
};

export function buildStepIndex(steps) {
  return (step) => {
    const i = steps.indexOf(step);
    return i === -1 ? 0 : i;
  };
}

/**
 * Prefers `GC_STEP_META[step].phase` when present; otherwise uses index ranges in the default 24-step layout.
 */
export function buildMainPhaseForStep(steps, stepMeta) {
  return (step) => {
    const m = stepMeta[step];
    if (m?.phase) return m.phase;
    const i = steps.indexOf(step);
    if (i === -1) return 'perm';
    if (i <= 11) return 'perm';
    if (i <= 14) return 'i140';
    if (i <= 19) return 'i485';
    return 'done';
  };
}

export function stepIndex(step) {
  return buildStepIndex(GC_STEPS)(step);
}

export function mainPhaseForStep(step) {
  return buildMainPhaseForStep(GC_STEPS, GC_STEP_META)(step);
}

/**
 * Loads step labels and order from API when available (Phase 2), else static exports above.
 */
export function useGcWorkflowConfig() {
  const { data, loading } = useImmigrationWorkflowDefinition('gc');

  return useMemo(() => {
    const linear = data?.linear;
    const upload = data?.uploadSlots;
    const stepMeta = data?.stepMeta;
    const st = linear?.map((r) => r.code) || GC_STEPS;
    const up = upload?.map((r) => r.code) || GC_UPLOAD_SLOTS;
    const meta = stepMeta || GC_STEP_META;
    return {
      GC_STEPS: st,
      GC_UPLOAD_SLOTS: up,
      GC_STEP_META: meta,
      stepIndex: buildStepIndex(st),
      mainPhaseForStep: buildMainPhaseForStep(st, meta),
      loading
    };
  }, [data, loading]);
}
